/**
 * Application Config service
 *
 * Fetches the application-level feature flags used by both cggenai-ui and
 * cg-agentic-ui. Callers pass a configured HttpClient (so each package keeps
 * its own auth + baseURL setup); the route + response decoding lives here.
 */

import type { HttpClient } from "../http";
import type {
  APIVersionInfoResponse,
  VersionAPIData,
  VersionFeatureInfo,
} from "../types/applicationConfig";

export const APPLICATION_CONFIG_ROUTE =
  "/api/marketing/content/v1/application-config";

/**
 * Fetches the application-config payload. Returns `null` when the request
 * fails so callers can treat "no flags" and "fetch error" uniformly without
 * tripping on unhandled rejections.
 */
export async function fetchApplicationConfig(
  client: HttpClient,
  route: string = APPLICATION_CONFIG_ROUTE,
): Promise<VersionAPIData | null> {
  try {
    const response = await client.get<APIVersionInfoResponse>(route);
    const data = (response as APIVersionInfoResponse)?.data;
    return data || null;
  } catch {
    return null;
  }
}

/**
 * Converts the `featureInfo` array from the API into a flag map for
 * convenient `features[name]` lookups.
 */
export function convertFeatureInfoToFeatures(
  featureInfo: VersionFeatureInfo[] | undefined | null,
): Record<string, boolean> {
  if (!featureInfo) return {};
  return featureInfo.reduce<Record<string, boolean>>((acc, f) => {
    acc[f.featureName] = !!f.enabled;
    return acc;
  }, {});
}
